import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { usePRD3012_R01Store } from "./Store_PRD3012_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { defaultPRD3012_R01LotDto, PRD3012_R01LotDto, PRD3012_R01InspectDto, PRD3012_R01DetailDto, defaultPRD3012_R01HeaderDto } from "./Dto_PRD3012_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { getAllWorkPlaces } from "src/apis/workPlaceApi";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { getAllWarehouses } from "src/apis/warehouseApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { DetailTable_PRD3012_R01 } from "./DetailTable_PRD3012_R01";

const COLUMN_STATE_KEY = 'PRD3012_R01-lot-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ['lotNo', 'lotQty'],
	insertedDisabledCells: [''],
	commonDisabledCells: [''],
	
	pointerCells: [''],
	insertedPointerCells: [''],
	commonPointerCells: [''],
	
	alignLeftCells: [''],
};

interface Props {
	lotGridRef: React.RefObject<AgGridReact<PRD3012_R01LotDto> | null>;
	defaultPRD3012_R01LotRow: PRD3012_R01LotDto;
}

export const LotTable_PRD3012_R01 = (props: Props) => {

	const { lotGridRef, defaultPRD3012_R01LotRow } = props;

	const { 
			PRD3012_R01Filter, setPRD3012_R01Filter,
			PRD3012_R01LotList, setPRD3012_R01LotList, 
			editPRD3012_R01, setEditPRD3012_R01,
			selectedPRD3012_R01LotList, setSelectedPRD3012_R01LotList, removeSelectedPRD3012_R01LotList, addSelectedPRD3012_R01LotList, initSelectedPRD3012_R01LotList,
			deletedPRD3012_R01LotList, setDeletedPRD3012_R01LotList, addDeletedPRD3012_R01LotList, removeDeletedPRD3012_R01LotList,
			updatePRD3012_R01LotList,
			lotColumnState, setLotColumnState,
			originalPRD3012_R01LotList, setOriginalPRD3012_R01LotList,
		} = usePRD3012_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { itemStatusList } = useCommonCodeStore();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef: lotGridRef,
		key: 'valueIndex',
		gridData: PRD3012_R01LotList,
		defaultGridData: defaultPRD3012_R01LotRow,
		selectedRow: selectedPRD3012_R01LotList,
		originalGridData: originalPRD3012_R01LotList,
		updateRow: (index: number, data: any) => updatePRD3012_R01LotList(index, data),
	});
	
	

	const headerNames: Record<string, string> = {
		rowIndex: "",
		lotQty: '생산량',
		lotCd: 'LOT No',
	}

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<PRD3012_R01LotDto>({
            idKey: 'valueIndex',
            selectedList: selectedPRD3012_R01LotList,
            deletedList: deletedPRD3012_R01LotList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedPRD3012_R01LotList, deletedPRD3012_R01LotList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);

	const baseColumnDefs: ColDef<PRD3012_R01LotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			// (params: PRD3012_R01InspectDto) => renderIndexCell(
			// 	params, 
			// 	(innerParams: PRD3012_R01LotDto) => getRowStyleFlags (
			// 		innerParams,
			// 		{
			// 			deletedList: deletedPRD3012_R01LotList,
			// 			selectedList: selectedPRD3012_R01LotList,
			// 			dataList: PRD3012_R01LotList
			// 		}),
			// 	(rowIndex: number, e: React.MouseEvent) => {},
			// 	(rowData) => RemoveRowHandler( 
			// 		lotGridRef,
			// 		PRD3012_R01LotList, 
			// 		rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
			// 		setClickable, 
			// 		removePRD3012_R01LotList, 
			// 		(valueIndex) => addDeletedPRD3012_R01LotList([valueIndex.toString()])
			// 	),
			// )
		),
		{
			headerName: '생산량', colId: 'lotQty', field: 'lotQty', width: 80,
			editable: (params: any) => params.data.inserted === true,
			pasteable: true,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
        {
            headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 150,
			editable: false,
		},
		// ...IndexParameterCell(),
	], [deletedPRD3012_R01LotList, PRD3012_R01LotList]);

	const columnDefs: ColDef<PRD3012_R01LotDto>[] = useMemo(() => {
		if (lotColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<PRD3012_R01LotDto>({
			baseColumnDefs,
			columnState: lotColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, lotColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: PRD3012_R01LotList,
		originalDataList: originalPRD3012_R01LotList,
		updateRow: updatePRD3012_R01LotList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, value) => {
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: PRD3012_R01LotList,
		idKey: 'valueIndex',
		selectedList: selectedPRD3012_R01LotList,
		deletedList: deletedPRD3012_R01LotList,
		addSelected: addSelectedPRD3012_R01LotList,
		removeSelected: removeSelectedPRD3012_R01LotList,
		clickable, setClickable
	});

	const commonInputColumns = ['lotNo', 'lotQty', 'remark', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['lotNo', 'lotQty', 'remark'];


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: PRD3012_R01LotList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<PRD3012_R01LotDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(PRD3012_R01LotList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<PRD3012_R01LotDto>[] = useMemo(() => [
	], [editPRD3012_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(PRD3012_R01LotList, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<PRD3012_R01LotDto>) => {
		postSortHandler(params, PRD3012_R01LotList, originalPRD3012_R01LotList, setPRD3012_R01LotList);
	}, [PRD3012_R01LotList, originalPRD3012_R01LotList, ]);

	const pinnedBottomRowData = useMemo(() => {
		// 삭제된 행은 제외하고 수량 합계 계산
		const totalQty : number = PRD3012_R01LotList
			.reduce((sum, item) => sum + (deletedPRD3012_R01LotList.includes(item.valueIndex.toString()) ? 0 : Number(item.lotQty) || 0), 0);

		return [{
			lotNo: PRD3012_R01LotList.length.toLocaleString() + "건",
			lotQty: totalQty,
		}];	  
	}, [PRD3012_R01LotList, deletedPRD3012_R01LotList,]);

	return (
		<>
			<div className="flex flex-1 gap-[4px] h-full min-h-full">
				<GridContextWrapper<PRD3012_R01LotDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={lotGridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setPRD3012_R01LotList}
					initSelected={initSelectedPRD3012_R01LotList}
					defaultRowData={defaultPRD3012_R01LotRow}
					baseColumnDefs={columnDefs}	
					setColumnState={setLotColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Excel<PRD3012_R01LotDto>
						ref={lotGridRef}
						rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
						columnDefs={columnDefs}		
						defaultColDef={gridDefaultColDef}

						rowClassRules={createRowClassRules<PRD3012_R01LotDto>({
							idKey: 'valueIndex',
							selectedList: selectedPRD3012_R01LotList,
							deletedList: deletedPRD3012_R01LotList
						})}

						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellDoubleClicked={handleCellDoubleClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						

						onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
						// Grid_Select에 공통 로직 처리를 위한 props 전달
						columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
						setColumnState={setLotColumnState}		//컬럼 상태 저장
					/>
				</GridContextWrapper>
			</div>
		</>
	)
}	